import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import scheduleServiceApiRaw from '../api/schedule.service.api';
import { getLang } from '../store/selectors/lang.selector';
import { ATTENDANCE_MODES, SCHEDULE_EVENT_TYPES } from '../constants/schedule.constants';
import {
  scheduleEventKeys,
  scheduleKeys,
  nextEventsPage,
  type SerializedScheduleKeyParams,
} from './schedule.keys';
import { startOfToday } from '../utils/date.utils';
import { pastEventsWindow } from '../utils/schedule-window';

// schedule.service.api.js is untyped (checkJs: false), so TS infers narrow param
// shapes from each method's defaults. Declare the methods this module uses with
// permissive params to consume it type-safely from a .ts hook. The api-client
// unwraps the envelope and throws on failure, so methods resolve to bare payloads.
interface ScheduleServiceApi {
  getScheduleEvents: (params: Record<string, unknown>) => Promise<EventsPage>;
  getScheduleFilters: () => Promise<{ filters: ScheduleFilter[] }>;
  getEventGroups: (params?: { type?: string }) => Promise<{ eventGroups: EventGroup[] }>;
  createEducationalEvent: (payload: Record<string, unknown>) => Promise<unknown>;
  createEvent: (payload: Record<string, unknown>) => Promise<unknown>;
  patchEvent: (params: { eventId: string; patch: Record<string, unknown> }) => Promise<unknown>;
  deleteEvent: (params: { eventId: string }) => Promise<unknown>;
  addEventAttendees: (params: { eventId: string; attendeeIds: string[] }) => Promise<unknown>;
  removeEventAttendees: (params: { eventId: string; attendeeIds: string[] }) => Promise<unknown>;
}

const scheduleServiceApi = scheduleServiceApiRaw as unknown as ScheduleServiceApi;

interface ScheduleEvent {
  id: string;
  date: string;
  startTime?: string | null;
  endTime?: string | null;
  [key: string]: unknown;
}

interface ParsedScheduleEvent extends Omit<ScheduleEvent, 'date' | 'startTime' | 'endTime'> {
  date: Date;
  startTime: Date | null;
  endTime: Date | null;
}

interface EventsPage {
  events: ScheduleEvent[];
  total: number;
  page: number;
  pageSize?: number;
}

interface ScheduleFilter {
  [key: string]: unknown;
}

interface EventGroup {
  id: string;
  [key: string]: unknown;
}

interface ScheduleDataParams {
  userId?: string;
  scheduleEventGroupIds?: string[];
  eventType?: string[];
  pastDays?: number;
  pastPageSize?: number;
  futurePageSize?: number;
  skip?: boolean;
}

const DEFAULT_PAST_DAYS = 30;

const parseEventDates = (event: ScheduleEvent): ParsedScheduleEvent => ({
  ...event,
  date: new Date(event.date),
  startTime: event.startTime ? new Date(event.startTime) : null,
  endTime: event.endTime ? new Date(event.endTime) : null,
});

export { scheduleKeys } from './schedule.keys';

// Stable, serializable key params — arrays joined so equal contents compare equal.
const eventsKeyParams = ({
  userId,
  scheduleEventGroupIds,
  eventType,
  pastDays = DEFAULT_PAST_DAYS,
  pastPageSize,
  futurePageSize,
}: ScheduleDataParams): SerializedScheduleKeyParams => ({
  userId: userId ?? null,
  scheduleEventGroupIds: scheduleEventGroupIds?.join(',') ?? '',
  eventType: eventType?.join(',') ?? '',
  pastDays,
  pastPageSize: pastPageSize ?? null,
  futurePageSize: futurePageSize ?? null,
});

/**
 * Fetches schedule data: recent past events (single desc request) plus future
 * events (paginated). Preserves the previous hook's return contract — combined
 * `events` (past oldest→newest, then future), `loadMore` for the next future page,
 * and the BE's authoritative `futureTotal`.
 *
 * Callers name the `eventType`s they render: the BE returns every type when the
 * param is omitted, including types the client has no list renderer for (the
 * BE's CASE events live under their parent assignment). No default on purpose.
 */
export function useScheduleData({
  userId,
  scheduleEventGroupIds,
  eventType,
  pastDays = DEFAULT_PAST_DAYS,
  pastPageSize,
  futurePageSize,
  skip = false,
}: ScheduleDataParams = {}) {
  const keyParams = eventsKeyParams({
    userId,
    scheduleEventGroupIds,
    eventType,
    pastDays,
    pastPageSize,
    futurePageSize,
  });

  const pastQuery = useQuery<ParsedScheduleEvent[]>({
    queryKey: scheduleKeys.pastEvents(keyParams),
    queryFn: () =>
      scheduleServiceApi
        .getScheduleEvents({
          userId,
          scheduleEventGroupIds,
          eventType,
          ...pastEventsWindow(pastDays),
          page: 1,
          pageSize: pastPageSize,
          sortOrder: 'desc',
        })
        .then(page => page.events.map(parseEventDates)),
    enabled: !skip,
  });

  const futureQuery = useInfiniteQuery<EventsPage>({
    queryKey: scheduleKeys.futureEvents(keyParams),
    queryFn: ({ pageParam = 1 }) =>
      scheduleServiceApi.getScheduleEvents({
        userId,
        scheduleEventGroupIds,
        eventType,
        startTime: startOfToday().toISOString(),
        page: pageParam as number,
        pageSize: futurePageSize,
        sortOrder: 'asc',
      }),
    initialPageParam: 1,
    getNextPageParam: nextEventsPage,
    enabled: !skip,
  });

  const futureTotal = futureQuery.data?.pages.at(-1)?.total ?? 0;

  // Memoize on the raw query data so the mapped/combined array keeps a stable
  // identity across unrelated re-renders — downstream FlatLists and
  // use-unified-schedule useMemo over it, and a churning reference defeats that.
  const events = useMemo((): ParsedScheduleEvent[] | null => {
    if (skip) return [];
    if (pastQuery.isLoading && futureQuery.isLoading) return null;
    const pastEvents = pastQuery.data ?? [];
    const futureEvents = (futureQuery.data?.pages ?? [])
      .flatMap(p => p.events)
      .map(parseEventDates);
    return [...[...pastEvents].reverse(), ...futureEvents];
  }, [skip, pastQuery.isLoading, futureQuery.isLoading, pastQuery.data, futureQuery.data]);

  return {
    events,
    loading: !skip && (pastQuery.isLoading || futureQuery.isLoading),
    loadingMore: futureQuery.isFetchingNextPage,
    loadMoreError: futureQuery.isFetchNextPageError ? futureQuery.error : null,
    refetching: pastQuery.isRefetching || futureQuery.isRefetching,
    hasMore: futureQuery.hasNextPage ?? false,
    error: pastQuery.error ?? futureQuery.error ?? null,
    futureTotal,
    loadMore: () => {
      if (futureQuery.hasNextPage && !futureQuery.isFetchingNextPage) futureQuery.fetchNextPage();
    },
    // `refetch()` runs even when `enabled` is false, and a skipped caller's
    // empty `eventType` would reach the BE as no filter at all.
    refetch: () => {
      if (skip) return;
      pastQuery.refetch();
      futureQuery.refetch();
    },
  };
}

/**
 * Organization's customized schedule filters. Fetched once and cached — filters
 * rarely change within a session.
 */
export function useCustomizedFilters() {
  const query = useQuery<ScheduleFilter[]>({
    queryKey: scheduleKeys.filters(),
    queryFn: () => scheduleServiceApi.getScheduleFilters().then(({ filters }) => filters ?? []),
    staleTime: Infinity,
  });

  return { customizedFilters: query.data ?? [], loading: query.isLoading };
}

export function useEventGroups({ type }: { type?: string } = {}) {
  const query = useQuery<EventGroup[]>({
    queryKey: scheduleKeys.groups(type),
    queryFn: () =>
      scheduleServiceApi.getEventGroups({ type }).then(({ eventGroups }) => eventGroups ?? []),
  });

  return {
    eventGroups: query.data ?? [],
    loading: query.isLoading,
    error: query.error ?? null,
    refetch: query.refetch,
  };
}

const invalidateSchedule = (qc: QueryClient) =>
  qc.invalidateQueries({ queryKey: scheduleKeys.all });

export function useCreateEducationalEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      scheduleServiceApi.createEducationalEvent(payload),
    onSuccess: () => invalidateSchedule(qc),
  });
}

export function useCreateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => scheduleServiceApi.createEvent(payload),
    onSuccess: () => invalidateSchedule(qc),
  });
}

const SHIFT_LOOKUP_PAGE_SIZE = 100;

export interface AssignPeopleInput {
  groupId: string;
  title: string;
  attendeeIds: string[];
  startTime: string;
  endTime: string;
  isAllDay: boolean;
  dayStart: string;
  nextDayStart: string;
}

export type AssignPeopleResult =
  { synced: true } | { synced: false; eventId: string | undefined; created: boolean };

/**
 * Assigns people to an assignment group's shift: when the group already has an event for that
 * shift, the people are added to it (the backend skips anyone already on it); otherwise the event
 * is created. An all-day shift is matched by its start, since uploads and the app end the day
 * differently. A synced event (`externalRef`) is never edited: that is an expected outcome, so
 * it resolves as `{ synced: true }` rather than reaching the error reporting.
 *
 * The mutation takes the shift (`startTime`, `endTime`, `isAllDay` from `buildAssignmentTimes`)
 * and the organization day it starts on (`dayStart`, `nextDayStart` from `localDayWindow`), which
 * bounds the lookup.
 *
 * @returns A mutation resolving to `{ synced: true }` when the shift's event is synced and nothing
 *   was written, else `{ synced: false, eventId, created }`.
 * @example
 * const assignPeople = useAssignPeople();
 * const result = await assignPeople.mutateAsync({
 *   groupId, title, attendeeIds,
 *   ...buildAssignmentTimes(date, details, timeZone),
 *   ...localDayWindow(date, timeZone),
 * });
 */
export function useAssignPeople() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      groupId,
      title,
      attendeeIds,
      startTime,
      endTime,
      isAllDay,
      dayStart,
      nextDayStart,
    }: AssignPeopleInput): Promise<AssignPeopleResult> => {
      const fetchPage = (page: number) =>
        scheduleServiceApi.getScheduleEvents({
          scheduleEventGroupIds: [groupId],
          eventType: [SCHEDULE_EVENT_TYPES.ASSIGNMENT],
          startTime: dayStart,
          startsBefore: nextDayStart,
          page,
          pageSize: SHIFT_LOOKUP_PAGE_SIZE,
        });
      // Uploads write one event per person, so a busy day can run past a page.
      const first = await fetchPage(1);
      const events = [...(first.events ?? [])];
      for (let page = 2; events.length < (first.total ?? 0); page += 1) {
        const { events: more = [] } = await fetchPage(page);
        if (!more.length) break;
        events.push(...more);
      }
      const instant = (value: unknown) => new Date(value as string).getTime();
      const sameShift = (event: (typeof events)[number]) =>
        instant(event.startTime) === instant(startTime) &&
        (isAllDay
          ? Boolean(event.isAllDay)
          : !event.isAllDay && instant(event.endTime) === instant(endTime));
      const matches = events.filter(sameShift);
      // The admin's own event wins a tie with a synced one, so the list order never decides.
      const existing = matches.find(event => !event.externalRef) ?? matches[0];
      if (existing?.externalRef) return { synced: true };
      if (existing) {
        await scheduleServiceApi.addEventAttendees({ eventId: existing.id, attendeeIds });
        return { synced: false, eventId: existing.id, created: false };
      }
      const created = (await scheduleServiceApi.createEvent({
        title,
        scheduleEventGroupId: groupId,
        attendeeIds,
        startTime,
        endTime,
        isAllDay,
      })) as { id?: string } | undefined;
      return { synced: false, eventId: created?.id, created: true };
    },
    onSuccess: result => {
      if (result.synced) return;
      invalidateSchedule(qc);
      if (result.eventId)
        qc.invalidateQueries({ queryKey: scheduleEventKeys.detail(result.eventId) });
    },
  });
}

export function usePatchEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId, patch }: { eventId: string; patch: Record<string, unknown> }) =>
      scheduleServiceApi.patchEvent({ eventId, patch }),
    onSuccess: () => invalidateSchedule(qc),
  });
}

export function useDeleteEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId }: { eventId: string }) => scheduleServiceApi.deleteEvent({ eventId }),
    onSuccess: () => invalidateSchedule(qc),
  });
}

export function useAddEventAttendees() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId, attendeeIds }: { eventId: string; attendeeIds: string[] }) =>
      scheduleServiceApi.addEventAttendees({ eventId, attendeeIds }),
    onSuccess: (_data, { eventId }) => {
      invalidateSchedule(qc);
      qc.invalidateQueries({ queryKey: scheduleEventKeys.detail(eventId) });
    },
  });
}

export function useRemoveEventAttendees() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId, attendeeIds }: { eventId: string; attendeeIds: string[] }) =>
      scheduleServiceApi.removeEventAttendees({ eventId, attendeeIds }),
    onSuccess: (_data, { eventId }) => {
      invalidateSchedule(qc);
      qc.invalidateQueries({ queryKey: scheduleEventKeys.detail(eventId) });
    },
  });
}

interface FormatOption {
  value: string;
  label: string | undefined;
}

export function useAttendanceModeLabels() {
  const lang = useSelector(getLang('EDUCATIONAL_EVENTS')) as Record<string, string>;

  const getFormatLabel = (mode: string): string | null | undefined => {
    switch (mode) {
      case ATTENDANCE_MODES.IN_PERSON:
        return lang.FORMAT_IN_PERSON;
      case ATTENDANCE_MODES.VIRTUAL:
        return lang.FORMAT_VIRTUAL;
      case ATTENDANCE_MODES.HYBRID:
        return lang.FORMAT_HYBRID;
      default:
        return null;
    }
  };

  const formatOptions: FormatOption[] = [
    { value: ATTENDANCE_MODES.IN_PERSON, label: lang.FORMAT_IN_PERSON },
    { value: ATTENDANCE_MODES.VIRTUAL, label: lang.FORMAT_VIRTUAL },
    { value: ATTENDANCE_MODES.HYBRID, label: lang.FORMAT_HYBRID },
  ];

  return { getFormatLabel, formatOptions };
}
